import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { Input, Button, Field, SortControl, sortRows } from "@/lib/ui";

const UNLABELED = "__unlabeled__";

export default function Leads() {
  const [labels, setLabels] = useState([]);
  const [leads, setLeads] = useState([]);
  const [newLabel, setNewLabel] = useState("");
  const [editLabel, setEditLabel] = useState(null); // {id, name}
  const [tplLabel, setTplLabel] = useState(null); // {id, name, message_template}
  const [dragId, setDragId] = useState(null);
  const [dragCard, setDragCard] = useState(null);
  const [noteEdit, setNoteEdit] = useState(null); // {id, note, followup_date}
  const [filterCompany, setFilterCompany] = useState("");
  const [filterCity, setFilterCity] = useState("");
  const [plannerStage, setPlannerStage] = useState("");
  const [plannerText, setPlannerText] = useState("");
  const [form, setForm] = useState({ name: "", phone: "", note: "", label_id: "" });
  const [search, setSearch] = useState("");
  const [csvFile, setCsvFile] = useState(null);
  const [csvLabel, setCsvLabel] = useState("");
  const [csvBusy, setCsvBusy] = useState(false);
  const [csvResult, setCsvResult] = useState(null);
  const [sort, setSort] = useState({ field: "name", dir: "asc" });
  const SORT_FIELDS = [
    { key: "updated_at", label: "Recent Activity", type: "date" },
    { key: "name", label: "Name", type: "str" },
    { key: "phone", label: "Phone", type: "str" },
  ];

  const load = useCallback(() => {
    api.get("/admin/lead-labels").then(({ data }) => setLabels(data.labels));
    api.get("/admin/leads").then(({ data }) => setLeads(data));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (!plannerStage && labels.length) setPlannerStage(labels[0].id); }, [labels, plannerStage]);
  useEffect(() => { const lb = labels.find((l) => l.id === plannerStage); setPlannerText(lb?.message_template || ""); }, [plannerStage, labels]);

  const addLabel = async (e) => {
    e.preventDefault();
    if (!newLabel.trim()) return;
    try { await api.post("/admin/lead-labels", { name: newLabel.trim(), message_template: "" }); setNewLabel(""); toast.success("Label added"); load(); }
    catch (er) { toast.error(apiErr(er)); }
  };
  const saveLabel = async () => {
    if (!editLabel.name.trim()) return;
    const lb = labels.find((x) => x.id === editLabel.id);
    try { await api.put(`/admin/lead-labels/${editLabel.id}`, { name: editLabel.name.trim(), message_template: lb?.message_template || "" }); setEditLabel(null); toast.success("Label updated"); load(); }
    catch (er) { toast.error(apiErr(er)); }
  };
  const saveTpl = async () => {
    try { await api.put(`/admin/lead-labels/${tplLabel.id}`, { name: tplLabel.name, message_template: tplLabel.message_template || "" }); setTplLabel(null); toast.success("Message template saved"); load(); }
    catch (er) { toast.error(apiErr(er)); }
  };
  const savePlanner = async () => {
    const lb = labels.find((l) => l.id === plannerStage);
    if (!lb) return;
    try { await api.put(`/admin/lead-labels/${lb.id}`, { name: lb.name, message_template: plannerText }); toast.success(`Message for "${lb.name}" saved`); load(); }
    catch (er) { toast.error(apiErr(er)); }
  };
  const delLabel = async (id) => {
    if (!window.confirm("Delete this label? Its leads move to Unlabeled.")) return;
    await api.delete(`/admin/lead-labels/${id}`); toast.success("Label deleted"); load();
  };
  const dropLabel = async (targetId) => {
    if (!dragId || dragId === targetId) { setDragId(null); return; }
    const ids = labels.map((l) => l.id);
    const from = ids.indexOf(dragId), to = ids.indexOf(targetId);
    if (from < 0 || to < 0) { setDragId(null); return; }
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    setLabels(ids.map((id) => labels.find((l) => l.id === id)));
    setDragId(null);
    try { await api.post("/admin/lead-labels/reorder", { ids }); toast.success("Order updated"); }
    catch (er) { toast.error(apiErr(er)); load(); }
  };
  const addLead = async (e) => {
    e.preventDefault();
    if (!form.phone.trim()) { toast.error("Phone is required"); return; }
    try { await api.post("/admin/leads", { ...form, label_id: form.label_id || (labels[0]?.id || "") }); setForm({ name: "", phone: "", note: "", label_id: "" }); toast.success("Lead added"); load(); }
    catch (er) { toast.error(apiErr(er)); }
  };
  const uploadCsv = async (e) => {
    e.preventDefault();
    if (!csvFile) { toast.error("Choose a CSV file first"); return; }
    const fd = new FormData();
    fd.append("file", csvFile);
    setCsvBusy(true);
    try {
      const { data } = await api.post(`/admin/leads/bulk-csv?label_id=${encodeURIComponent(csvLabel)}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCsvResult(data); setCsvFile(null);
      toast.success(`${data.added} added, ${data.updated} updated`);
      load();
    } catch (er) { toast.error(apiErr(er)); }
    finally { setCsvBusy(false); }
  };
  const downloadTemplate = async () => {
    const { data } = await api.get("/admin/leads/csv-template", { responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a"); a.href = url; a.download = "leads_template.csv"; a.click();
    URL.revokeObjectURL(url);
  };
  const moveLead = async (lead, labelId) => {
    setLeads((ls) => ls.map((l) => (l.id === lead.id ? { ...l, label_id: labelId } : l)));
    try { await api.put(`/admin/leads/${lead.id}`, { label_id: labelId }); } catch (er) { toast.error(apiErr(er)); load(); }
  };
  const delLead = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    await api.delete(`/admin/leads/${id}`); load();
  };
  const saveNote = async () => {
    try {
      await api.put(`/admin/leads/${noteEdit.id}`, { note: noteEdit.note, followup_date: noteEdit.followup_date || "" });
      setLeads((ls) => ls.map((l) => (l.id === noteEdit.id ? { ...l, note: noteEdit.note, followup_date: noteEdit.followup_date } : l)));
      setNoteEdit(null); toast.success("Note saved");
    } catch (er) { toast.error(apiErr(er)); }
  };
  const clearFollowup = async (lead) => {
    try {
      await api.put(`/admin/leads/${lead.id}`, { followup_date: "" });
      setLeads((ls) => ls.map((l) => (l.id === lead.id ? { ...l, followup_date: "" } : l)));
      toast.success("Follow-up cleared");
    } catch (er) { toast.error(apiErr(er)); }
  };

  const visible = sortRows(
    leads.filter((l) => {
      const s = search.trim().toLowerCase();
      const okS = !s || [l.name, l.phone, l.last_product, l.last_company, l.last_city, l.note].some((v) => (v || "").toLowerCase().includes(s));
      const okC = !filterCompany || l.last_company === filterCompany;
      const okCity = !filterCity || l.last_city === filterCity;
      return okS && okC && okCity;
    }),
    SORT_FIELDS, sort
  );
  const companies = [...new Set(leads.map((l) => l.last_company).filter(Boolean))].sort();
  const cities = [...new Set(leads.map((l) => l.last_city).filter(Boolean))].sort();
  const todayStr = new Date().toLocaleDateString("en-CA");
  const dueLeads = leads
    .filter((l) => l.followup_date && l.followup_date <= todayStr)
    .sort((a, b) => a.followup_date.localeCompare(b.followup_date));
  const columns = [...labels, { id: "", name: "Unlabeled", count: leads.filter((l) => !l.label_id).length, unlabeled: true }];
  const leadsOf = (labelId) => visible.filter((l) => (l.label_id || "") === labelId);

  const waHref = (lead) => {
    const lb = labels.find((x) => x.id === (lead.label_id || ""));
    const tpl = lb?.message_template || "";
    if (!tpl) return `https://wa.me/91${lead.phone}`;
    const msg = tpl
      .replace(/\{name\}/gi, lead.name || "there")
      .replace(/\{product\}/gi, lead.last_product || "the stock you enquired about")
      .replace(/\{company\}/gi, lead.last_company || "the seller");
    return `https://wa.me/91${lead.phone}?text=${encodeURIComponent(msg)}`;
  };

  return (
    <div>
      <h1 className="font-head text-2xl font-extrabold text-slate-900">Leads</h1>
      <p className="mt-1 text-sm text-slate-500">Sellers are captured here automatically as they register and move through your funnel. Create labels below — each becomes a button to move a lead across stages. Drag the <i className="fa-solid fa-grip-vertical text-slate-400" /> handle to reorder stages, and tap the <i className="fa-brands fa-whatsapp text-emerald-500" /> on a label to set the auto message sent when you WhatsApp a lead in that stage.</p>

      {dueLeads.length > 0 && (
        <div className="mt-4 rounded-2xl border border-orange-200 bg-orange-50 p-4" data-testid="lead-followups-banner">
          <div className="mb-2 flex items-center gap-2">
            <i className="fa-solid fa-bell text-orange-600" />
            <span className="font-head font-bold text-orange-800">Follow-ups due today ({dueLeads.length})</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {dueLeads.map((lead) => {
              const overdue = lead.followup_date < todayStr;
              return (
                <div key={lead.id} className="flex items-center gap-3 rounded-xl border border-orange-200 bg-white px-3 py-2 text-sm shadow-sm" data-testid={`followup-${lead.id}`}>
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-slate-800">{lead.name || "+91 " + lead.phone}</p>
                    <p className={`text-xs font-semibold ${overdue ? "text-red-600" : "text-orange-600"}`}>{overdue ? "Overdue" : "Today"} · {lead.followup_date}{lead.last_company ? ` · ${lead.last_company}` : ""}</p>
                  </div>
                  <a href={waHref(lead)} target="_blank" rel="noreferrer" data-testid={`followup-wa-${lead.id}`} className="inline-flex items-center gap-1 rounded-full bg-emerald-500 px-2.5 py-1 text-xs font-semibold text-white hover:bg-emerald-600"><i className="fa-brands fa-whatsapp" /> WhatsApp</a>
                  <button onClick={() => clearFollowup(lead)} data-testid={`followup-done-${lead.id}`} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-200"><i className="fa-solid fa-check" /> Done</button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Label planner */}
      <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4">
        <p className="mb-2 text-sm font-bold uppercase tracking-wide text-slate-500">Label Planner</p>
        <form onSubmit={addLabel} className="flex flex-wrap items-center gap-2">
          <Input className="max-w-xs" placeholder="New label name (e.g. Follow-up)" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} data-testid="lead-newlabel" />
          <Button type="submit" data-testid="lead-addlabel"><i className="fa-solid fa-plus" /> Add Label</Button>
          <div className="ml-2 flex flex-wrap gap-2">
            {labels.map((lb) => (
              <span key={lb.id}
                draggable={editLabel?.id !== lb.id}
                onDragStart={() => setDragId(lb.id)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => dropLabel(lb.id)}
                className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-sm transition ${dragId === lb.id ? "border-orange-400 bg-orange-50 opacity-60" : "border-slate-200 bg-slate-50"}`}>
                {editLabel?.id === lb.id ? (
                  <>
                    <input autoFocus value={editLabel.name} onChange={(e) => setEditLabel((s) => ({ ...s, name: e.target.value }))} className="w-24 rounded border border-slate-300 px-1 text-sm" />
                    <button onClick={saveLabel} data-testid={`lead-label-save-${lb.id}`} className="text-emerald-600"><i className="fa-solid fa-check" /></button>
                    <button onClick={() => setEditLabel(null)} className="text-slate-400"><i className="fa-solid fa-xmark" /></button>
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-grip-vertical cursor-grab text-slate-300 hover:text-slate-500" data-testid={`lead-label-drag-${lb.id}`} title="Drag to reorder" />
                    <span className="font-semibold text-slate-700">{lb.name}</span>
                    <button onClick={() => setTplLabel({ id: lb.id, name: lb.name, message_template: lb.message_template || "" })} data-testid={`lead-label-tpl-${lb.id}`} className="text-emerald-500 hover:text-emerald-700" title="Edit WhatsApp message"><i className="fa-brands fa-whatsapp text-xs" /></button>
                    <button onClick={() => setEditLabel({ id: lb.id, name: lb.name })} data-testid={`lead-label-edit-${lb.id}`} className="text-slate-400 hover:text-slate-700"><i className="fa-solid fa-pen text-xs" /></button>
                    <button onClick={() => delLabel(lb.id)} data-testid={`lead-label-del-${lb.id}`} className="text-red-400 hover:text-red-600"><i className="fa-solid fa-trash text-xs" /></button>
                  </>
                )}
              </span>
            ))}
          </div>
        </form>
      </div>

      {/* Message template planner */}
      <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4" data-testid="lead-msg-planner">
        <p className="mb-1 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-500"><i className="fa-brands fa-whatsapp text-emerald-500" /> Message Template Planner</p>
        <p className="mb-3 text-xs text-slate-400">Pick a stage and plan the WhatsApp message that auto-fills when you message a lead in that stage. Placeholders: <code className="rounded bg-slate-100 px-1">{"{name}"}</code>, <code className="rounded bg-slate-100 px-1">{"{product}"}</code>, <code className="rounded bg-slate-100 px-1">{"{company}"}</code>.</p>
        <div className="grid gap-3 sm:grid-cols-[220px_1fr]">
          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500">Stage</label>
            <select value={plannerStage} onChange={(e) => setPlannerStage(e.target.value)} data-testid="lead-planner-stage" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm">
              {labels.map((lb) => <option key={lb.id} value={lb.id}>{lb.name}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500">Message</label>
            <textarea rows={4} value={plannerText} onChange={(e) => setPlannerText(e.target.value)} data-testid="lead-planner-text"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="e.g. Hi {name}, following up on {product}…" />
          </div>
        </div>
        <div className="mt-3 flex justify-end">
          <Button onClick={savePlanner} data-testid="lead-planner-save"><i className="fa-solid fa-check" /> Save Message</Button>
        </div>
      </div>

      {/* Manual add lead */}
      <form onSubmit={addLead} className="mt-4 grid gap-3 rounded-2xl border border-slate-200 bg-white p-4 sm:grid-cols-5">
        <Field label="Name"><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="lead-name" /></Field>
        <Field label="Phone"><Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value.replace(/\D/g, "").slice(0, 10) }))} data-testid="lead-phone" /></Field>
        <Field label="Note"><Input value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} data-testid="lead-note" /></Field>
        <Field label="Label"><select value={form.label_id} onChange={(e) => setForm((f) => ({ ...f, label_id: e.target.value }))} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"><option value="">{labels[0]?.name || "Unlabeled"}</option>{labels.map((lb) => <option key={lb.id} value={lb.id}>{lb.name}</option>)}</select></Field>
        <div className="flex items-end"><Button type="submit" className="w-full" data-testid="lead-add"><i className="fa-solid fa-user-plus" /> Add Lead</Button></div>
      </form>

      {/* Bulk CSV upload */}
      <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4" data-testid="lead-csv-panel">
        <p className="mb-1 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-500"><i className="fa-solid fa-file-csv text-orange-600" /> Bulk Upload Leads (CSV)</p>
        <p className="mb-3 text-xs text-slate-400">Columns: <code className="rounded bg-slate-100 px-1">name</code>, <code className="rounded bg-slate-100 px-1">phone</code> (required, 10 digits), <code className="rounded bg-slate-100 px-1">note</code>, <code className="rounded bg-slate-100 px-1">label</code>, <code className="rounded bg-slate-100 px-1">company</code>, <code className="rounded bg-slate-100 px-1">city</code>. Existing phone numbers are updated, not duplicated.</p>
        <form onSubmit={uploadCsv} className="flex flex-wrap items-end gap-3">
          <Field label="CSV file">
            <input type="file" accept=".csv,text/csv" key={csvFile ? "f" : "e"} onChange={(e) => setCsvFile(e.target.files?.[0] || null)} data-testid="lead-csv-file"
              className="block text-sm text-slate-600 file:mr-3 file:rounded-full file:border-0 file:bg-orange-50 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-orange-700 hover:file:bg-orange-100" />
          </Field>
          <Field label="Default label (if CSV has none)">
            <select value={csvLabel} onChange={(e) => setCsvLabel(e.target.value)} data-testid="lead-csv-label" className="rounded-lg border border-slate-300 px-3 py-2 text-sm">
              <option value="">Unlabeled</option>{labels.map((lb) => <option key={lb.id} value={lb.id}>{lb.name}</option>)}
            </select>
          </Field>
          <Button type="submit" disabled={csvBusy} data-testid="lead-csv-upload"><i className={`fa-solid ${csvBusy ? "fa-spinner fa-spin" : "fa-upload"}`} /> {csvBusy ? "Uploading…" : "Upload CSV"}</Button>
          <Button type="button" variant="outline" onClick={downloadTemplate} data-testid="lead-csv-template"><i className="fa-solid fa-download" /> Template</Button>
        </form>
        {csvResult && (
          <div className="mt-3 rounded-xl bg-slate-50 p-3 text-sm" data-testid="lead-csv-result">
            <p className="font-semibold text-slate-700">{csvResult.total_rows} rows · <span className="text-emerald-700">{csvResult.added} added</span> · <span className="text-blue-700">{csvResult.updated} updated</span> · {csvResult.skipped_duplicates} duplicate{csvResult.skipped_duplicates === 1 ? "" : "s"} skipped · <span className={csvResult.error_count ? "text-red-600" : ""}>{csvResult.error_count} error{csvResult.error_count === 1 ? "" : "s"}</span></p>
            {csvResult.errors.length > 0 && <ul className="mt-1 list-disc pl-5 text-xs text-red-600">{csvResult.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>}
            <button onClick={() => setCsvResult(null)} className="mt-1 text-xs text-slate-400 hover:text-slate-600">Dismiss</button>
          </div>
        )}
      </div>

      {/* Search + filters + sort */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Input className="max-w-xs" placeholder="Search mobile / company / city / name…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="lead-search" />
        <select value={filterCompany} onChange={(e) => setFilterCompany(e.target.value)} data-testid="lead-filter-company" className="rounded-lg border border-slate-300 px-3 py-2 text-sm"><option value="">All companies</option>{companies.map((c) => <option key={c} value={c}>{c}</option>)}</select>
        <select value={filterCity} onChange={(e) => setFilterCity(e.target.value)} data-testid="lead-filter-city" className="rounded-lg border border-slate-300 px-3 py-2 text-sm"><option value="">All cities</option>{cities.map((c) => <option key={c} value={c}>{c}</option>)}</select>
        <SortControl fields={SORT_FIELDS} value={sort} onChange={setSort} testid="lead-sort" />
        {(search || filterCompany || filterCity) && <Button variant="outline" onClick={() => { setSearch(""); setFilterCompany(""); setFilterCity(""); }} data-testid="lead-filter-clear"><i className="fa-solid fa-xmark" /> Clear</Button>}
      </div>

      {/* Kanban board */}
      <div className="mt-5 flex gap-4 overflow-x-auto pb-4">
        {columns.map((col) => {
          const items = leadsOf(col.id);
          return (
            <div key={col.id || "unlabeled"}
              onDragOver={(e) => { if (dragCard || !col.unlabeled) e.preventDefault(); }}
              onDrop={() => {
                if (dragCard) { const lead = leads.find((l) => l.id === dragCard); if (lead && (lead.label_id || "") !== (col.id || "")) moveLead(lead, col.id); setDragCard(null); }
                else if (!col.unlabeled) dropLabel(col.id);
              }}
              className={`w-72 flex-shrink-0 rounded-2xl p-3 transition ${dragId === col.id ? "bg-orange-100 ring-2 ring-orange-300" : dragCard ? "bg-slate-100 ring-2 ring-dashed ring-orange-200" : "bg-slate-100"}`} data-testid={`lead-col-${col.id || "unlabeled"}`}>
              <div className="mb-3 flex items-center justify-between px-1">
                <span className="flex items-center gap-2 font-head font-bold text-slate-800">
                  {!col.unlabeled && <i draggable onDragStart={() => setDragId(col.id)} onDragEnd={() => setDragId(null)} className="fa-solid fa-grip-vertical cursor-grab text-slate-400 hover:text-slate-600" data-testid={`lead-col-drag-${col.id}`} title="Drag to move column" />}
                  {col.name}
                </span>
                <span className="rounded-full bg-orange-600 px-2 py-0.5 text-xs font-bold text-white" data-testid={`lead-count-${col.id || "unlabeled"}`}>{items.length}</span>
              </div>
              <div className="space-y-2">
                {items.length === 0 && <p className="px-1 text-xs text-slate-400">No leads</p>}
                {items.map((lead, idx) => (
                  <div key={lead.id}
                    draggable
                    onDragStart={(e) => { e.stopPropagation(); setDragCard(lead.id); }}
                    onDragEnd={() => setDragCard(null)}
                    className={`cursor-grab rounded-xl border border-slate-200 bg-white p-3 shadow-sm transition active:cursor-grabbing ${dragCard === lead.id ? "opacity-40" : ""}`} data-testid={`lead-card-${lead.id}`}>
                    <div className="flex items-start justify-between">
                      <div className="min-w-0">
                        <p className="flex items-center gap-2 truncate font-semibold text-slate-800"><span className="inline-flex h-5 min-w-[1.25rem] flex-shrink-0 items-center justify-center rounded-md bg-slate-800 px-1 text-[10px] font-bold text-white" data-testid={`lead-sno-${lead.id}`}>{idx + 1}</span><span className="truncate">{lead.name || "+91 " + lead.phone}</span></p>
                        {lead.name && <p className="text-xs text-slate-500">+91 {lead.phone}</p>}
                        {lead.last_company && <p className="mt-0.5 truncate text-xs text-slate-400">↳ {lead.last_product} · {lead.last_company}{lead.last_city ? ` · ${lead.last_city}` : ""}</p>}
                        {lead.note && <p className="mt-1 flex items-start gap-1 text-xs text-slate-600"><i className="fa-regular fa-note-sticky mt-0.5 text-slate-400" />{lead.note}</p>}
                        {lead.followup_date && <p className="mt-0.5 text-xs font-semibold text-orange-600"><i className="fa-regular fa-calendar mr-1" />Follow-up: {lead.followup_date}</p>}
                      </div>
                      <div className="flex flex-shrink-0 items-center gap-2 pl-1">
                        <button onClick={() => setNoteEdit({ id: lead.id, note: lead.note || "", followup_date: lead.followup_date || "" })} className="text-slate-300 hover:text-orange-500" data-testid={`lead-note-edit-${lead.id}`} title="Note / follow-up"><i className="fa-solid fa-pen text-xs" /></button>
                        <button onClick={() => delLead(lead.id)} className="text-slate-300 hover:text-red-500" data-testid={`lead-del-${lead.id}`}><i className="fa-solid fa-trash text-xs" /></button>
                      </div>
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <a href={waHref(lead)} target="_blank" rel="noreferrer" data-testid={`lead-wa-${lead.id}`}
                        className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-100">
                        <i className="fa-brands fa-whatsapp" /> WhatsApp
                      </a>
                      <span className="text-[10px] uppercase text-slate-300">{lead.source === "manual" ? "manual" : lead.source === "csv" ? "csv" : lead.source === "referor" ? "referor" : "click"}</span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1 border-t border-slate-100 pt-2">
                      {columns.filter((c) => (c.id || "") !== (lead.label_id || "")).map((c) => (
                        <button key={c.id || "u"} onClick={() => moveLead(lead, c.id)} data-testid={`lead-move-${lead.id}-${c.id || "unlabeled"}`}
                          className="rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600 hover:bg-orange-600 hover:text-white">
                          → {c.name}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {tplLabel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" data-testid="lead-tpl-modal">
          <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-xl">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-head text-lg font-bold text-slate-900">WhatsApp message — <span className="text-orange-600">{tplLabel.name}</span></h3>
              <button onClick={() => setTplLabel(null)} className="text-slate-400 hover:text-slate-700"><i className="fa-solid fa-xmark" /></button>
            </div>
            <p className="mb-2 text-xs text-slate-500">This message is auto-filled when you tap WhatsApp on any lead in this stage. Use placeholders <code className="rounded bg-slate-100 px-1">{"{name}"}</code>, <code className="rounded bg-slate-100 px-1">{"{product}"}</code>, <code className="rounded bg-slate-100 px-1">{"{company}"}</code>.</p>
            <textarea rows={5} value={tplLabel.message_template} onChange={(e) => setTplLabel((s) => ({ ...s, message_template: e.target.value }))} data-testid="lead-tpl-text"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="e.g. Hi {name}, following up on {product}..." />
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setTplLabel(null)}>Cancel</Button>
              <Button onClick={saveTpl} data-testid="lead-tpl-save"><i className="fa-solid fa-check" /> Save Message</Button>
            </div>
          </div>
        </div>
      )}
      {noteEdit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" data-testid="lead-note-modal">
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-head text-lg font-bold text-slate-900">Lead Note & Follow-up</h3>
              <button onClick={() => setNoteEdit(null)} className="text-slate-400 hover:text-slate-700"><i className="fa-solid fa-xmark" /></button>
            </div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500">Note</label>
            <textarea rows={3} value={noteEdit.note} onChange={(e) => setNoteEdit((s) => ({ ...s, note: e.target.value }))} data-testid="lead-note-text"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Quick note about this lead…" />
            <label className="mb-1 mt-3 block text-xs font-bold uppercase tracking-wide text-slate-500">Next follow-up date</label>
            <input type="date" value={noteEdit.followup_date || ""} onChange={(e) => setNoteEdit((s) => ({ ...s, followup_date: e.target.value }))} data-testid="lead-note-date"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setNoteEdit(null)}>Cancel</Button>
              <Button onClick={saveNote} data-testid="lead-note-save"><i className="fa-solid fa-check" /> Save</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
